import Papa from "papaparse";
import { downloadCsv } from "src/utils/download";
import { buildCsvFilename } from "src/utils/csv-filename";

import { DIMENSION_LABELS } from "./types";
import type {
  ChartConfig,
  GridChartResult,
  LabelResolver,
  SendInsightDimension,
} from "./types";

type CsvRow = Record<string, string | number>;

export function exportGridChartAsCsv(args: {
  flowName: string;
  config: ChartConfig;
  result: GridChartResult;
  resolveLabel: LabelResolver;
}): void {
  const { flowName, config, result, resolveLabel } = args;
  const { cells } = result;

  if (cells.length === 0) return;

  const segmentKeys = Object.keys(cells[0]!.segments);
  const rowKey = segmentKeys[0]!;
  const colKey = segmentKeys[1];

  const rowDimLabel =
    DIMENSION_LABELS[rowKey as SendInsightDimension] ?? rowKey;
  const includeCampaignIds = rowKey === "message_id";

  // Preserve first-appearance order so CSV row/column order matches what the
  // backend returned (which is also what the heatmap renders pre-sort).
  const rowOrder: string[] = [];
  const rowSeen = new Set<string>();
  const colOrder: string[] = [];
  const colSeen = new Set<string>();

  type RowMeta = { id: string; name: string; campaignIds?: string[] };
  const rowMetaByName = new Map<string, RowMeta>();
  const valuesByRow = new Map<string, Map<string, number | null>>();

  for (const cell of cells) {
    const rawRow = cell.segments[rowKey];
    if (rawRow == null) continue;
    const rowLabel = resolveLabel(rowKey as SendInsightDimension, rawRow);

    if (!rowSeen.has(rowLabel.name)) {
      rowSeen.add(rowLabel.name);
      rowOrder.push(rowLabel.name);
      rowMetaByName.set(rowLabel.name, {
        id: rowLabel.id ?? String(rawRow),
        name: rowLabel.name,
        campaignIds: rowLabel.campaignIds,
      });
      valuesByRow.set(rowLabel.name, new Map());
    }

    if (colKey != null) {
      const rawCol = cell.segments[colKey];
      if (rawCol == null) continue;
      const colLabel = resolveLabel(colKey as SendInsightDimension, rawCol);
      if (!colSeen.has(colLabel.name)) {
        colSeen.add(colLabel.name);
        colOrder.push(colLabel.name);
      }
      valuesByRow.get(rowLabel.name)!.set(colLabel.name, cell.value);
    } else {
      valuesByRow.get(rowLabel.name)!.set("Value", cell.value);
    }
  }

  const valueColumns = colKey != null ? colOrder : ["Value"];

  const idColumn = `${rowDimLabel} ID`;
  const nameColumn = rowDimLabel;
  const staticColumns = includeCampaignIds
    ? [idColumn, nameColumn, "Campaign IDs"]
    : [idColumn, nameColumn];
  const columns = [...staticColumns, ...valueColumns];

  const rows: CsvRow[] = rowOrder.map((rowName) => {
    const meta = rowMetaByName.get(rowName)!;
    const values = valuesByRow.get(rowName)!;
    const row: CsvRow = {
      [idColumn]: meta.id,
      [nameColumn]: meta.name,
    };
    if (includeCampaignIds) {
      row["Campaign IDs"] = meta.campaignIds?.join(", ") ?? "";
    }
    for (const col of valueColumns) {
      const v = values.get(col);
      // Blank (not 0) when the cell is missing or null, so "no data" stays
      // distinguishable from a true 0 — mirrors the heatmap's "–" placeholder
      // and the convention used by exportMessagesPerformanceAsCsv.
      row[col] = v == null ? "" : v;
    }
    return row;
  });

  const csvString = Papa.unparse(rows, { columns });
  downloadCsv(csvString, buildCsvFilename(flowName, config.id));
}
